import { useRef } from "react";
import EmbedFlow from "@formsort/react-embed";
import { createFileRoute, Link } from "@tanstack/react-router";
import { v7 as uuidv7 } from "uuid";

import { ScreenSpinner, TimedLoader } from "@/components";
import { analytics } from "@/config/analytics";
import { useCheckSurveyCompletion } from "@/domains/profile/mutations/use-check-survey-completion";
import { surveyQueryOptions } from "@/domains/surveys/factories/survey-query-options";
import { surveySearchSchema } from "@/domains/surveys/models/schemas";
import { selectPostSurveyRoute } from "@/domains/surveys/utils/post-survey-route";
import { env } from "@/env";
import { Heading } from "@/ui";
import { useSurveyQueryParams } from "./-hooks/use-survey-query-params";

const Survey = () => {
  const queryParams = useSurveyQueryParams();
  const responderUuid = useRef(uuidv7()).current;

  const {
    data: destination,
    isIdle,
    isPending,
    mutate: checkSurveyCompletion,
  } = useCheckSurveyCompletion();

  const loaderDestination = selectPostSurveyRoute(destination);

  if (!isIdle) {
    return (
      <TimedLoader destination={loaderDestination} shouldAutoComplete={!isPending}>
        <div className="flex flex-col gap-6">
          <Heading className="text-xl sm:text-3xl" level={1}>
            {`Thanks! We're updating your plan.`}
          </Heading>
          <p className="text-xl font-medium">
            Do not refresh this page. This should take no more than 40 seconds.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <Heading level={2}>{`What's Next?`}</Heading>
          <ul className="list-inside list-disc text-xl font-medium">
            <li>Review your updated plan</li>
            <li>Order your products</li>
            <li>Use &amp; provide feedback</li>
          </ul>
        </div>
      </TimedLoader>
    );
  }

  return (
    <div className="relative flex-1">
      <div className="absolute inset-0">
        <EmbedFlow
          clientLabel={env.VITE_FORMSORT_SURVEY_CLIENT_LABEL}
          embedConfig={{ style: { width: "100%", height: "100%" } }}
          flowLabel={env.VITE_FORMSORT_SURVEY_FLOW_LABEL}
          formsortEnv={env.VITE_FORMSORT_SURVEY_ENV}
          onFlowFinalized={() => {
            analytics.track("flow_finalized", {
              flow_label: env.VITE_FORMSORT_SURVEY_FLOW_LABEL,
              responder_uuid: responderUuid,
            });
            checkSurveyCompletion();
          }}
          onFlowLoaded={() => {
            analytics.track("flow_loaded", {
              flow_label: env.VITE_FORMSORT_SURVEY_FLOW_LABEL,
              responder_uuid: responderUuid,
            });
          }}
          onStepCompleted={({ stepId, stepIndex }) => {
            if (!stepId || stepIndex === undefined) {
              return;
            }
            analytics.track("step_completed", {
              flow_label: env.VITE_FORMSORT_SURVEY_FLOW_LABEL,
              responder_uuid: responderUuid,
              step_id: stepId,
              step_index: stepIndex,
            });
          }}
          queryParams={queryParams}
          responderUuid={responderUuid}
          variantLabel={env.VITE_FORMSORT_SURVEY_VARIANT_LABEL}
        />
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_private/survey/")({
  validateSearch: surveySearchSchema,
  loader: async ({ context }) => {
    return await context.queryClient.ensureQueryData(surveyQueryOptions.params);
  },
  errorComponent: () => {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <Heading level={1}>
          No feedback needed at this time. Return to your care plan{" "}
          <Link className="underline" to="/careplan">
            here
          </Link>
          .
        </Heading>
      </div>
    );
  },
  pendingComponent: () => {
    return <ScreenSpinner />;
  },
  component: Survey,
});
